import { EditingCategory, NoUploadEditingKinds, ProductKind, ProductKindsWithPFDocumentUpload, UnlimitedUploadKinds } from 'constants/product'
import { FileAction, FileState } from 'components/common/FileAPI'
import { MeetingFieldsErrors, usePurchaseFlowOrderMeta } from '../contexts/PurchaseFlowOrderMeta.context'
import { isDocumentCategory, isShootingCategory } from 'utils/validators'
import { usePurchaseFlowPaymentStatus } from '../../steps/Validation/_main/contexts'

import { PurchaseFlowStepKey, usePurchaseFlowUtilities } from '../contexts/PurchaseFlowUtilities.context'
import { useBookCreative } from '../../common'
import { useCart } from '../contexts/Cart.context'
import { useEmbedConsent } from '../contexts/EmbedConsent.context'
import { useFloorPlanConfig } from '../../steps/FloorPlanConfig/_main/FloorPlanConfigStep.context'
import { usePFInvoicingAddresses } from '../contexts/InvoicingAddress.context'
import { usePurchaseFlowConfig } from '../contexts/PurchaseFlowConfig.context'
import { usePurchaseFlowProducts } from '../contexts/PurchaseFlowProducts.context'
import { useUploadedPurchaseFlowVisuals } from '../contexts/UploadedPurchaseFlowVisuals.context'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'

/** A field added to MeetingFieldsErrors fails to compile until it gets its code here. */
const meetingFieldErrors: Record<keyof MeetingFieldsErrors, NextStepValidationError> = {
  date: 'meeting_date_invalid',
  name: 'meeting_name_invalid',
  phone: 'meeting_phone_invalid',
  email: 'meeting_email_invalid',
  address: 'meeting_address_invalid',
}

/**
 * Why the next-step button of a step is greyed out (ISSUE-3955): the validation_errors of
 * Clicks_next_step_purchase_flow. Mirrors the step validators of PurchaseFlowUtilities, which only keep a yes/no:
 * the main steps of the embed (product, instructions, billing, validation) and the chain before them report
 * detailed codes, every other step 'step_incomplete'. A detailed step whose validator blocks without any of
 * these codes reports 'unexplained_block'. Each code belongs to one step, so a code from an earlier step says
 * where the broker has to go back to.
 */
export const useNextStepValidationErrors = () => {
  const {
    isUserStepValid,
    isAddressStepValid,
    isCategoryStepValid,
    isProductStepValid,
    isStagingStepValid,
    isInstructionStepValid,
    isBillingStepValid,
    isInstructionStepSkipped,
  } = usePurchaseFlowUtilities()
  const { selectedCategory } = usePurchaseFlowConfig()
  const { selectedProducts, requiresAIEditingConfig, selectedSkyReplacement } = usePurchaseFlowProducts()
  const { uploadedImagesByProduct, uploadInProgress, documentFilesController } = useUploadedPurchaseFlowVisuals()
  const { floorPlanProductSettings } = useFloorPlanConfig()
  const { selectedMeetingType, currentFieldErrors, selectedExtraServices, selectedPaymentMethod, isStripePaymentMethod } = usePurchaseFlowOrderMeta()
  const { selectedCreative } = useBookCreative()
  const { total } = useCart()
  const { selectedInvoicingAddress, recalculatePricesMutation } = usePFInvoicingAddresses()
  const { isConsentBlockingOrder } = useEmbedConsent()
  const { isReceiptEmailValid, selectedSavedPaymentMethod, stripeInputElementsAreValid } = usePurchaseFlowPaymentStatus()

  const getProductErrors = (): NextStepValidationError[] => {
    const products = Object.values(selectedProducts)
    if (!products.length) return ['product_missing']
    if (selectedCategory && (isShootingCategory(selectedCategory) || isDocumentCategory(selectedCategory))) return []
    if (uploadInProgress) return ['upload_in_progress']

    const errors = new Set<NextStepValidationError>()
    const imageCountError = (count: number, quantity: number) => {
      if (count < quantity) errors.add('images_missing')
      if (count > quantity) errors.add('too_many_images')
    }

    for (const product of products) {
      const imageCount = Object.keys(uploadedImagesByProduct[product.id] || {}).length

      if (selectedCategory === EditingCategory.STAGING) {
        imageCountError(imageCount, product.quantity)
        continue
      }
      if (selectedCategory !== EditingCategory.EDITING) continue

      if (product.kind === ProductKind.FLOOR_PLAN_EDITING_2D) {
        const settings = floorPlanProductSettings.get(product.id)
        if (!settings || settings.drawOnlySpecificUnit === undefined) errors.add('floor_plan_unit_missing')
        else if (settings.drawOnlySpecificUnit && settings.drawOnlySpecificUnitComment.trim().length === 0) errors.add('floor_plan_unit_comment_missing')
      }

      if (NoUploadEditingKinds.has(product.kind)) continue
      if (UnlimitedUploadKinds.has(product.kind)) {
        if (imageCount < product.quantity) errors.add('images_missing')
        continue
      }
      if (ProductKindsWithPFDocumentUpload.has(product.kind)) {
        const files = documentFilesController.filesByTag[product.id] ?? []
        if (!files.some((file) => file.action === FileAction.UPLOAD && file.state === FileState.SUCCESS)) errors.add('document_missing')
        continue
      }

      imageCountError(imageCount, product.quantity)
      if (requiresAIEditingConfig && !selectedSkyReplacement) errors.add('sky_replacement_missing')
    }

    return Array.from(errors)
  }

  const getInstructionErrors = (): NextStepValidationError[] => {
    const errors: NextStepValidationError[] = []

    if (selectedCategory && isShootingCategory(selectedCategory)) {
      if (!selectedMeetingType) errors.push('meeting_type_missing')
      else {
        for (const field of Object.keys(meetingFieldErrors) as Array<keyof MeetingFieldsErrors>) {
          if (currentFieldErrors[field]) errors.push(meetingFieldErrors[field])
        }
      }
    }
    if (selectedExtraServices.has(ProductKind.FAVOURITE_CREATIVE) && !selectedCreative) errors.push('favourite_creative_missing')

    return errors
  }

  const getBillingErrors = (): NextStepValidationError[] => {
    const errors: NextStepValidationError[] = []
    if (!selectedPaymentMethod && total.totalAfterDiscount.gt(0)) errors.push('payment_method_missing')
    if (!selectedInvoicingAddress || selectedInvoicingAddress === 'new') errors.push('invoicing_address_missing')
    return errors
  }

  const getValidationErrors = (): NextStepValidationError[] => {
    const errors: NextStepValidationError[] = []
    if (isConsentBlockingOrder) errors.push('consent_missing')
    if (recalculatePricesMutation.isPending) errors.push('prices_recalculating')
    if (isStripePaymentMethod && !isReceiptEmailValid) errors.push('receipt_email_invalid')
    if (isStripePaymentMethod && selectedSavedPaymentMethod === null && !stripeInputElementsAreValid) errors.push('card_details_incomplete')
    return errors
  }

  /**
   * A detailed step that is blocked must report at least one code. None means the validator has a rule these codes
   * don't mirror: 'unexplained_block' makes that drift visible instead of hiding it in 'step_incomplete'.
   */
  const blocked = (errors: NextStepValidationError[]): NextStepValidationError[] =>
    errors.length ? errors : ['unexplained_block']

  /** Each case first follows the same chain as its validator, so the first missing piece is the one reported. */
  const getNextStepValidationErrors = (stepKey: string): NextStepValidationError[] => {
    switch (stepKey) {
      case PurchaseFlowStepKey.CLIENT:
        return blocked(['client_missing'])
      case PurchaseFlowStepKey.ADDRESS:
        if (!isUserStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.CLIENT)
        return blocked(['address_missing'])
      case PurchaseFlowStepKey.CATEGORY:
        if (!isAddressStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.ADDRESS)
        return blocked(['category_missing'])
      case PurchaseFlowStepKey.PRODUCT:
        if (!isCategoryStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.CATEGORY)
        return blocked(getProductErrors())
      case PurchaseFlowStepKey.STYLE_SECTION:
        if (!isProductStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.PRODUCT)
        return blocked(['rooms_incomplete'])
      case PurchaseFlowStepKey.INSTRUCTIONS:
        if (!isStagingStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.STYLE_SECTION)
        if (!selectedCategory) return blocked(['category_missing'])
        return blocked(getInstructionErrors())
      case PurchaseFlowStepKey.BILLING:
        if (!isInstructionStepSkipped && !isInstructionStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.INSTRUCTIONS)
        return blocked(getBillingErrors())
      case PurchaseFlowStepKey.VALIDATION:
        if (!isBillingStepValid) return getNextStepValidationErrors(PurchaseFlowStepKey.BILLING)
        return blocked(getValidationErrors())
      default:
        return ['step_incomplete']
    }
  }

  return getNextStepValidationErrors
}
